import { useEffect, useRef, type MouseEvent } from 'react';
import {
  TextField,
  ToggleButton,
  FormField,
  formFieldRequiredLabel as requiredLabel,
  FlexColumn,
  PasswordField,
} from '@elementary/common-ui/components';
import { ToggleButtonGroup } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import {
  DatabricksAuthMethod,
  DatabricksStorageAccessType,
} from 'shared/types';
import { RoleArnAuth, SecretAccessKeyAuth } from '../AuthTypeFormFields';

const DATABRICKS_STORAGE_ACCESS_UI_NONE = 'none' as const;
const DATABRICKS_STORAGE_ACCESS_UI_DIRECT = 'direct' as const;

type DatabricksStorageAccessUiTop =
  | typeof DATABRICKS_STORAGE_ACCESS_UI_NONE
  | typeof DATABRICKS_STORAGE_ACCESS_UI_DIRECT
  | DatabricksStorageAccessType.CREDENTIALS_VENDING;

const TokenAuth = ({ disabled }: { disabled?: boolean }) => {
  const methods = useFormContext();
  const {
    register,
    formState: { dirtyFields, errors },
  } = methods;

  return (
    <FormField
      title="Access token"
      errorMessage={errors.databricksAccessToken?.message as string}
    >
      <PasswordField
        {...register('databricksAccessToken', {
          required: requiredLabel,
        })}
        placeholder="Token"
        isFormValueDirty={dirtyFields.databricksAccessToken}
        clearFormValue={() => methods.setValue('databricksAccessToken', '')}
        disabled={disabled}
        size="large"
      />
    </FormField>
  );
};

const OAuthAuth = ({ disabled }: { disabled?: boolean }) => {
  const methods = useFormContext();
  const {
    register,
    formState: { dirtyFields, errors },
  } = methods;

  return (
    <>
      <FormField
        title="Client ID"
        errorMessage={errors.databricksClientId?.message as string}
      >
        <TextField
          {...register('databricksClientId', {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder="e.g. 00000000-0000-0000-0000-000000000000"
          disabled={disabled}
          size="large"
        />
      </FormField>
      <FormField
        title="Client secret"
        errorMessage={errors.databricksClientSecret?.message as string}
      >
        <PasswordField
          {...register('databricksClientSecret', {
            required: requiredLabel,
          })}
          placeholder="Client secret"
          isFormValueDirty={dirtyFields.databricksClientSecret}
          clearFormValue={() => methods.setValue('databricksClientSecret', '')}
          disabled={disabled}
          size="large"
        />
      </FormField>
    </>
  );
};

const StorageAccessSection = ({ disabled }: { disabled?: boolean }) => {
  const methods = useFormContext();
  const storageAccessType = useWatch({
    name: 'databricksStorageAccessType',
  }) as DatabricksStorageAccessType | null | undefined;

  const topLevelSelection: DatabricksStorageAccessUiTop =
    storageAccessType == null
      ? DATABRICKS_STORAGE_ACCESS_UI_NONE
      : storageAccessType === DatabricksStorageAccessType.CREDENTIALS_VENDING
      ? DatabricksStorageAccessType.CREDENTIALS_VENDING
      : DATABRICKS_STORAGE_ACCESS_UI_DIRECT;

  const directAuthSubtype: DatabricksStorageAccessType =
    storageAccessType === DatabricksStorageAccessType.ROLE_ARN
      ? DatabricksStorageAccessType.ROLE_ARN
      : DatabricksStorageAccessType.ACCESS_KEYS;

  useEffect(() => {
    if (
      topLevelSelection === DATABRICKS_STORAGE_ACCESS_UI_DIRECT &&
      storageAccessType !== DatabricksStorageAccessType.ACCESS_KEYS &&
      storageAccessType !== DatabricksStorageAccessType.ROLE_ARN
    ) {
      methods.setValue(
        'databricksStorageAccessType',
        DatabricksStorageAccessType.ACCESS_KEYS,
      );
    }
  }, [topLevelSelection, storageAccessType, methods]);

  const handleTopLevelChange = (
    _: MouseEvent<HTMLElement>,
    val: DatabricksStorageAccessUiTop | null,
  ) => {
    if (val == null) {
      return;
    }
    if (val === DATABRICKS_STORAGE_ACCESS_UI_NONE) {
      methods.setValue('databricksStorageAccessType', undefined);
    } else if (val === DatabricksStorageAccessType.CREDENTIALS_VENDING) {
      methods.setValue(
        'databricksStorageAccessType',
        DatabricksStorageAccessType.CREDENTIALS_VENDING,
      );
    } else if (val === DATABRICKS_STORAGE_ACCESS_UI_DIRECT) {
      const current = methods.getValues('databricksStorageAccessType');
      if (
        current !== DatabricksStorageAccessType.ACCESS_KEYS &&
        current !== DatabricksStorageAccessType.ROLE_ARN
      ) {
        methods.setValue(
          'databricksStorageAccessType',
          DatabricksStorageAccessType.ACCESS_KEYS,
        );
      }
    }
  };

  return (
    <>
      <FormField title="Storage access method" optional>
        <ToggleButtonGroup
          fullWidth
          value={topLevelSelection}
          onChange={handleTopLevelChange}
          exclusive
          disabled={disabled}
        >
          <ToggleButton value={DATABRICKS_STORAGE_ACCESS_UI_NONE} size="large">
            None
          </ToggleButton>
          <ToggleButton
            value={DatabricksStorageAccessType.CREDENTIALS_VENDING}
            size="large"
          >
            Credentials vending
          </ToggleButton>
          <ToggleButton
            value={DATABRICKS_STORAGE_ACCESS_UI_DIRECT}
            size="large"
          >
            Direct storage access
          </ToggleButton>
        </ToggleButtonGroup>
      </FormField>

      {topLevelSelection === DATABRICKS_STORAGE_ACCESS_UI_DIRECT && (
        <>
          <FormField title="Select S3 authentication method">
            <ToggleButtonGroup
              fullWidth
              value={directAuthSubtype}
              onChange={(_, val: DatabricksStorageAccessType | null) => {
                if (val) {
                  methods.setValue('databricksStorageAccessType', val);
                }
              }}
              exclusive
              disabled={disabled}
            >
              <ToggleButton
                value={DatabricksStorageAccessType.ACCESS_KEYS}
                size="large"
              >
                Secret access key
              </ToggleButton>
              <ToggleButton
                value={DatabricksStorageAccessType.ROLE_ARN}
                size="large"
              >
                AWS role ARN
              </ToggleButton>
            </ToggleButtonGroup>
          </FormField>

          {directAuthSubtype === DatabricksStorageAccessType.ACCESS_KEYS && (
            <SecretAccessKeyAuth
              keyIdFormKey="databricksStorageAccessKeyId"
              secretKeyFormKey="databricksStorageSecretAccessKey"
              sessionTokenFormKey="databricksStorageSessionToken"
              disabled={disabled}
            />
          )}

          {directAuthSubtype === DatabricksStorageAccessType.ROLE_ARN && (
            <RoleArnAuth
              roleArnFormKey="databricksStorageRoleArn"
              externalIdFormKey="databricksStorageExternalId"
              disabled={disabled}
            />
          )}
        </>
      )}
    </>
  );
};

const DatabricksFormContent = ({ disabled }: { disabled?: boolean }) => {
  const selectedAuthMethodRef = useRef<HTMLDivElement>(null);
  const methods = useFormContext();
  const {
    register,
    formState: { errors },
  } = methods;
  const databricksAuthMethod: DatabricksAuthMethod =
    useWatch({ name: 'databricksAuthMethod' }) ?? DatabricksAuthMethod.OAUTH;

  useEffect(() => {
    selectedAuthMethodRef.current?.scrollIntoView({
      behavior: 'smooth',
    });
  }, [databricksAuthMethod]);

  return (
    <>
      <FormField
        title="Server host"
        errorMessage={errors.databricksHost?.message as string}
      >
        <TextField
          {...register('databricksHost', {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder="e.g. 000-00000-0000.cloud.databricks.com"
          disabled={disabled}
          size="large"
        />
      </FormField>
      <FormField
        title="http path"
        errorMessage={errors.databricksHttpPath?.message as string}
      >
        <TextField
          {...register('databricksHttpPath', {
            required: requiredLabel,
          })}
          variant="outlined"
          placeholder="e.g. /sql/1.0/endpoints/00000000"
          disabled={disabled}
          size="large"
        />
      </FormField>

      <FormField title="Select authentication method">
        <Controller
          control={methods.control}
          name="databricksAuthMethod"
          defaultValue={DatabricksAuthMethod.OAUTH}
          render={({ field }) => (
            <ToggleButtonGroup
              fullWidth
              value={field.value}
              onChange={(_, val: DatabricksAuthMethod | null) =>
                val && field.onChange(val)
              }
              exclusive
              disabled={disabled}
            >
              <ToggleButton value={DatabricksAuthMethod.OAUTH} size="large">
                OAuth (M2M)
              </ToggleButton>
              <ToggleButton value={DatabricksAuthMethod.TOKEN} size="large">
                Access token (legacy)
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        />
      </FormField>

      <FlexColumn gap="inherit" ref={selectedAuthMethodRef}>
        {databricksAuthMethod === DatabricksAuthMethod.TOKEN && (
          <TokenAuth disabled={disabled} />
        )}

        {databricksAuthMethod === DatabricksAuthMethod.OAUTH && (
          <OAuthAuth disabled={disabled} />
        )}
      </FlexColumn>

      <StorageAccessSection disabled={disabled} />
    </>
  );
};

export default DatabricksFormContent;
